/**
 * isiZulu: Broadcasts
 *
 * What this file covers: Messages to many contacts at once: audiences, scheduling, costs, delivery reports.
 *
 * Tone: Careful and exact about who receives what and what it costs. Numbers, counts and dates must stay
 * exactly as written.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const broadcasts: Catalog = {
  'broadcasts.title': 'Ukusakaza',
  'broadcasts.actions.new': 'Ukusakaza okusha',
  'broadcasts.loadError': 'Ayikwazanga ukulayisha ukusakaza.',
  'broadcasts.empty': 'Akukho ukusakaza okwamanje.',
  'broadcasts.columns.template': 'Isifanekiso',
  'broadcasts.columns.sentTotal': 'Kuthunyelwe / Isamba',
  'broadcasts.form.phoneNumber': 'Inombolo yocingo',
  'broadcasts.form.noNumbers': 'Azikho izinombolo ezinamathiselwe',
  'broadcasts.form.templateLanguage': 'Ulimi lwesifanekiso',
  'broadcasts.form.recipients': 'Abamukeli ({{count}})',
  'broadcasts.form.phoneNumberRequired': 'Khetha inombolo yocingo.',
  'broadcasts.form.templateRequired': 'Khetha isifanekiso.',
  'broadcasts.form.recipientsRequired': 'Engeza okungenani umamukeli oyedwa.',
  'broadcasts.form.schedule': 'Hlela ukusakaza',
  'broadcasts.toast.recipientsTitle': 'Abamukeli',
  'broadcasts.toast.noOptedIn': 'Abekho abaxhumanayo abavumile abatholakele.',
  'broadcasts.toast.createFailed': 'Ukudala kwehlulekile',
  'broadcasts.view.notFound': 'Ukusakaza akutholakali.',
  'broadcasts.view.backToAll': 'Konke ukusakaza',
  'broadcasts.view.tabs.stats': 'Izibalo',
  'broadcasts.view.tabs.lifecycle': 'Umjikelezo wempilo',
  'broadcasts.view.overview.recipientsHint': 'abamukeli',
  'broadcasts.view.overview.filtered': 'Kuhlungiwe',
  'broadcasts.view.overview.progress': 'Inqubekelaphambili',
  'broadcasts.view.overview.throttle': 'Isivinini',
  'broadcasts.view.overview.timing': 'Isikhathi',
  'broadcasts.view.overview.finished': 'Kuqediwe',
  'broadcasts.view.stats.title': 'Ukwehlukaniswa kokulethwa',
  'broadcasts.view.lifecycle.createdDraft': 'Kudalwe (uhlaka)',
  'broadcasts.view.lifecycle.started': 'Kuqaliwe',
  'broadcasts.view.lifecycle.live': 'bukhoma',
  'broadcasts.view.lifecycle.pending': 'kulindile',
  'broadcasts.view.lifecycle.startNow': 'Qala manje',
  'broadcasts.view.lifecycle.cancelBroadcast': 'Khansela ukusakaza',
  'broadcasts.view.lifecycle.modal.cancel.title': 'Khansela lokhu kusakaza?',
  'broadcasts.view.lifecycle.modal.cancel.keep': 'Qhubeka usebenza',
  'broadcasts.view.lifecycle.toast.started': 'Ukusakaza kuqalile.',
  'broadcasts.view.lifecycle.toast.startFailed': 'Ayikwazanga ukuqala.',
  'broadcasts.view.lifecycle.toast.cancelled': 'Ukusakaza kukhanseliwe.',
  'broadcasts.view.lifecycle.toast.cancelFailed': 'Ayikwazanga ukukhansela.',
  'broadcasts.form.audience.tag': 'Ithegi',
  'broadcasts.form.tag': 'Ithegi',
  'broadcasts.form.tag.option': '{{tag}} ({{count}})',
  'broadcasts.form.tagRequired': 'Khetha ithegi ozothumela kuyo.',
  'broadcasts.form.tagCount': 'Lokhu kuzofinyelela kubantu abangu-{{count}} abamakwe ngo-{{tag}}.',
  'broadcasts.new.title': 'Ukusakaza okusha',
  'broadcasts.new.description': 'Khetha ukuthi kuya kubani, kuthini nokuthi nini. Akukho okuthunyelwayo ngaphambi kwesinyathelo sokugcina.',
  'broadcasts.new.step.audience': 'Izethameli',
  'broadcasts.new.step.message': 'Umlayezo',
  'broadcasts.new.step.when': 'Nini',
  'broadcasts.new.step.review': 'Buyekeza bese uthumela',
  'broadcasts.new.action.send': 'Thumela manje',
  'broadcasts.new.action.schedule': 'Kuhlele',
  'broadcasts.new.sent.now': 'Kuthunyelwa kubamukeli abangu-{{count}}.',
  'broadcasts.new.sent.scheduled': 'Kuhlelelwe abamukeli abangu-{{count}}.',
  'broadcasts.new.previewFailed': 'Izethameli azikwazanga ukubalwa okwamanje. Izibalo ezingezansi zingase zibe zindala.',
  'broadcasts.new.draft.saved': 'Uhlaka lulondolozwe ngo-{{time}}',
  'broadcasts.new.draft.saving': 'Kulondolozwa uhlaka…',
  'broadcasts.new.draft.unavailable': 'Lolu hlaka alulondolozwa. Luqedele kulelithebhu.',
  'broadcasts.new.draft.resumed': 'Kuqhutshekiwe ngohlaka lwakho olulondoloziwe.',
  'broadcasts.new.draft.discard': 'Lahla uhlaka',
  'broadcasts.new.draft.discard.title': 'Lahla lolu hlaka?',
  'broadcasts.new.draft.discard.body': 'Konke okugcwaliswe kuze kube manje kuyalahlwa. Akukho okuthunyelwe.',
  'broadcasts.new.leave.title': 'Ushiya lokhu kusakaza?',
  'broadcasts.new.leave.body': 'Akukathunyelwa. Uhlaka lwakho lulondoloziwe, ngakho ungaqhubeka lapho ugcine khona.',
  'broadcasts.new.leave.discard': 'Hamba',
  'broadcasts.new.leave.keep': 'Hlala lapha',
  'broadcasts.new.refusal.recipients_required': 'Khetha ukuthi lokhu kuya kubani.',
  'broadcasts.new.refusal.tag_empty': 'Akekho onalelo thegi osavumile ukwamukela, ngakho lokhu bekungeke kufinyelele muntu.',
  'broadcasts.new.refusal.segment_empty': 'Leso siqephu asifani namuntu osangathunyelwa umlayezo.',
  'broadcasts.new.refusal.segment_too_broad': 'Leso siqephu asinamithetho, ngakho sifana nawo wonke umuntu onaye. Engeza umthetho ngaphambi kokuthumela kuso.',
  'broadcasts.new.refusal.audience_all_opted_out': 'Wonke umuntu kulezi zethameli ucele ukungamukeli imilayezo.',
  'broadcasts.new.audience.numberHint': 'Inombolo lezi zilayezo ezizovela kuyo.',
  'broadcasts.new.audience.noNumbers': 'Azikho izinombolo ezixhunyiwe okwamanje. Xhuma enye ngaphambi kokuthumela.',
  'broadcasts.new.audience.noSegments': 'Awunazo iziqephu ezilondoloziwe okwamanje. Thumela kuthegi esikhundleni salokho, noma londoloza isiqephu ekhasini lokuxhumana.',
  'broadcasts.new.audience.willReceive': 'bazokwamukela lokhu',
  'broadcasts.new.audience.breakdown': '{{matched}} kulezi zethameli, {{excluded}} bakhishiwe.',
  'broadcasts.new.audience.optedOut': 'Abangu-{{count}} kubo bacele ukungamukeli imilayezo, ngakho bayashiywa. Ababalwa futhi akukhokhelwa ngabo.',
  'broadcasts.new.audience.large.title': 'Lezo yizethameli ezinkulu',
  'broadcasts.new.audience.large.body': 'Lokhu kuya kubantu abangu-{{count}} futhi akukwazi ukubuyiswa uma sekuqalile. Hlola izethameli ngaphambi kokuthumela.',
  'broadcasts.new.audience.cannotSend': 'Akukwazi ukuthunyelwa kulezi zethameli',
  'broadcasts.new.audience.counting': 'Kubalwa izethameli…',
  'broadcasts.new.message.noneApproved.title': 'Awekho amathempulethi agunyaziwe okwamanje',
  'broadcasts.new.message.noneApproved.body': 'Ukusakaza kuthumela ithempulethi eligunyazwe yi-WhatsApp, hhayi umbhalo okhululekile. Bhala elilodwa ulithumele ligunyazwe, bese ubuya.',
  'broadcasts.new.message.noneApproved.action': 'Dala ithempulethi',
  'broadcasts.new.message.noPlaceholders': 'Leli thempulethi alinazo izikhala, ngakho lithumela amagama afanayo kuwo wonke umuntu.',
  'broadcasts.new.message.previewEmpty': 'Leli thempulethi alinawo umbhalo womlayezo.',
  'broadcasts.new.message.previewBusiness': 'Ibhizinisi lakho',
  'broadcasts.new.message.previewFor': 'Yilokhu {{who}} azokwamukela.',
  'broadcasts.new.message.previewNoContact': 'Khetha izethameli ukuze ubuke lokhu ngothintana naye ongokoqobo.',
  'broadcasts.new.message.fillWith': 'Gcwalisa isikhala {{index}} nge-',
  'broadcasts.new.message.fill.literal': 'Amagama afanayo kuwo wonke umuntu',
  'broadcasts.new.message.field.name': 'Igama lothintana naye',
  'broadcasts.new.message.field.email': 'I-imeyili yothintana naye',
  'broadcasts.new.message.field.locale': 'Ulimi lothintana naye',
  'broadcasts.new.message.field.msisdn': 'Inombolo yothintana naye',
  'broadcasts.new.message.fallback': 'Uma bengenayo',
  'broadcasts.new.message.fallbackHint': 'I-WhatsApp iyawenqaba umlayezo onesikhala esingenalutho, ngakho yilokhu okuzohamba kunoma ubani ongenalutho lapho.',
  'broadcasts.new.message.personalised': 'Umuntu ngamunye ubona okwakhe lapha.',
  'broadcasts.new.when.label': 'Kufanele kuphume nini lokhu?',
  'broadcasts.new.when.now': 'Thumela manje',
  'broadcasts.new.when.nowHint': 'Kuqala ukuphuma ngokushesha lapho ucindezela u-Thumela.',
  'broadcasts.new.when.later': 'Thumela ngesikhathi esimisiwe',
  'broadcasts.new.when.laterHint': 'Kulinda kuze kufike isikhathi osikhethile, bese kuziqalela.',
  'broadcasts.new.when.zoneHint': 'Kufundwa ngokwesikhathi senhlangano yakho, {{zone}}.',
  'broadcasts.new.when.goesOut': 'Kuphuma {{when}} ({{zone}}).',
  'broadcasts.new.when.yourClock': 'Ngewashi lakho lokhu ngu-{{when}} ({{zone}}).',
  'broadcasts.new.when.differentZone': 'Inhlangano yakho isethelwe ku-{{org}} kanti le divayisi ise-{{browser}}. Isikhathi esingenhla ngesenhlangano yakho.',
  'broadcasts.new.when.required': 'Khetha usuku nesikhathi, noma uthumele manje.',
  'broadcasts.new.when.past': 'Leso sikhathi sesidlulile. Khetha esinye esilandelayo, noma uthumele manje.',
  'broadcasts.new.review.recipients': 'Abamukeli',
  'broadcasts.new.review.recipientsHint': '{{matched}} kulezi zethameli, {{excluded}} bacele ukuyeka',
  'broadcasts.new.review.cost': 'Izindleko ezilinganiselwe',
  'broadcasts.new.review.costHint': 'ngemilayezo engu-{{count}}, ngamanani akho abhalisiwe',
  'broadcasts.new.review.costUnknown': 'ayitholakali okwamanje',
  'broadcasts.new.review.balance': 'Ibhalansi yesikhwama',
  'broadcasts.new.review.balanceHint': 'cishe u-{{after}} osele ngemva kwalokho',
  'broadcasts.new.review.balanceUnknown': 'ayitholakali okwamanje',
  'broadcasts.new.review.at': '{{when}} ({{zone}})',
  'broadcasts.new.review.immediately': 'Ngokushesha lapho ucindezela u-Thumela',
  'broadcasts.new.review.runsOut.title': 'Uma isikhwama siphela phakathi',
  'broadcasts.new.review.runsOut.body': 'Amakhredithi ahlolwa umlayezo nomlayezo. Uma ibhalansi iphela phakathi, abantu asebefinyelelwe bahlala befinyelelwe, abanye balinde, futhi akukho okunye okuphumayo kuze ube ungezelela.',
  'broadcasts.new.review.topUp': 'Yengeza esikhwameni sakho',
  'broadcasts.new.review.noPreview': 'Izibalo zalokhu kusakaza azikwazanga ukubalwa. Buyela emuva isinyathelo esisodwa uhlole izethameli.',
  'broadcasts.new.leave.body.unsaved': 'Akukathunyelwa, futhi lolu hlaka alulondolozwa, ngakho ukuhamba kulahla konke okugcwalisile.',
  'broadcasts.view.recipients.attempts': 'imizamo engu-{{count}}',
  'broadcasts.recipientError.INSUFFICIENT_CREDITS': 'Bekungekho ikhredithi eyanele esikhwameni ngesikhathi lo myalezo uthunyelwa.',
  'broadcasts.recipientError.PHONE_NOT_FOUND': 'Inombolo ethumelayo ayisasebenzi kule akhawunti.',
  'broadcasts.recipientError.ACCOUNT_NOT_PAYABLE': 'I-WhatsApp iyenqabile ngenxa yenkinga yokukhokha ngakithi, hhayi ngakuwe.',
  'broadcasts.recipientError.WINDOW_CLOSED': 'I-WhatsApp ivumela umyalezo ojwayelekile kuphela phakathi kwamahora angu-24 emva kwempendulo yokugcina yomuntu.',
  'broadcasts.recipientError.UNDELIVERABLE': 'I-WhatsApp ayikwazanga ukuthumela kule nombolo.',
  'broadcasts.recipientError.NUMBER_NOT_REGISTERED': 'Le nombolo ayikho ku-WhatsApp.',
  'broadcasts.recipientError.META_REFUSED': 'I-WhatsApp ayiwamukelanga lo myalezo.',
  'broadcasts.recipientError.SEND_FAILED': 'Umzamo wangaphambili wokuthumela lo myalezo uhlulekile.',
  'broadcasts.recipientError.DISPATCH_GAVE_UP': 'Ayikwazanga ukudluliselwa ku-WhatsApp emva kwemizamo emithathu. Hlola inombolo nesikhwama, bese uthumela kulo muntu futhi.',
  'broadcasts.recipientError.UNEXPECTED_RESPONSE': 'Isevisi yemiyalezo inikeze impendulo esingakwazanga ukuyifunda.',
  'broadcasts.new.message.literalHint': 'Thayipha u-{{ ukuze ugcwalise lokhu kusuka koxhumana naye, njengegama lakhe.',
  'broadcasts.new.review.placeholder': 'Indawo yokubamba {{index}}'
};

export default broadcasts;
